// Turning a typed params object into a §06 query string.
//
// The API's list conventions are uniform on purpose — `?q=`, repeatable
// `?sort=field:asc|desc`, `?limit`/`?offset`, `field=` filters (repeatable, and
// OR-ed by the API) and `field_gte`/`field_lte` ranges — so the client that
// builds them should be uniform too. One builder, exercised by tests, beats a
// hand-rolled `URLSearchParams` dance per screen: an unknown or misspelled
// filter is a 400 with `unsupported_filter`, not a silently ignored parameter,
// so getting the encoding right matters.

import type { SortDirection, SortSpec } from "@crm/shared";

/**
 * What a caller may hand a query param. `undefined` and `null` mean "no filter"
 * — NOT "filter for empty" — so they are dropped. An array becomes a repeated
 * parameter, which is how the API expresses OR.
 */
export type QueryParamValue =
  | string
  | number
  | boolean
  | readonly string[]
  | readonly number[]
  | null
  | undefined;

export type QueryParams = Readonly<Record<string, QueryParamValue>>;

function serializeScalar(value: string | number | boolean): string | null {
  if (typeof value === "number") {
    // A NaN offset would reach the API as the literal "NaN" and come back as a
    // validation failure that reads like a server bug.
    return Number.isFinite(value) ? String(value) : null;
  }
  if (typeof value === "boolean") return String(value);
  return value.trim() === "" ? null : value;
}

/**
 * Build `?a=1&b=x&b=y`, or `""` when every param was empty. Returning the `?`
 * as part of the result means callers concatenate unconditionally and never
 * produce a trailing bare `?`.
 */
export function buildQueryString(params: QueryParams): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;

    if (Array.isArray(value)) {
      for (const entry of value as readonly (string | number)[]) {
        const serialized = serializeScalar(entry);
        if (serialized !== null) search.append(key, serialized);
      }
      continue;
    }

    const serialized = serializeScalar(value as string | number | boolean);
    if (serialized !== null) search.append(key, serialized);
  }

  const query = search.toString();
  return query === "" ? "" : `?${query}`;
}

/** `{ field: "due_date", direction: "desc" }` -> `"due_date:desc"`. */
export function serializeSort(sort: SortSpec): string {
  return `${sort.field}:${sort.direction}`;
}

/** `?sort=` is repeatable; the API applies the specs in the order given. */
export function serializeSorts(sorts: readonly SortSpec[]): string[] {
  return sorts.map(serializeSort);
}

/** Parse `"due_date:desc"` back into a spec — for sort state held in the URL. */
export function parseSort(raw: string): SortSpec | null {
  const [field, direction] = raw.split(":");
  if (field === undefined || field === "") return null;
  if (direction !== "asc" && direction !== "desc") return null;
  return { field, direction: direction satisfies SortDirection };
}
